import { useRouteLoaderData, useParams, useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import EditNoteIcon from '@mui/icons-material/EditNote'
import { useTranslation } from 'react-i18next'
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium'

import { LoaderData, SubEvents, SubEventType } from '../../../types'
import {
  formatDateTime,
  useSubEventsTypeOptions,
  useSubEventsStatusTypeOptions,
  useRoomsDataOptions,
  getOption,
  useLocationDataOptions,
} from '../../../utils'
import { useLazyGetSubEventQRCodeQuery } from '../../../services/events'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import SubEventStatusTag from '../../events/components/SubEventStatusTag'
import { useContextShowStatistics } from '../Admin.Context'
import { adminEventDetailsLoader } from '../Admin.loader'
import ViewButtonGroup from './components/ViewButtonGroup'
import AdminEventDetails from './components/AdminEventDetails'

const initialState: GridInitialState = {
  sorting: {
    sortModel: [
      {
        field: 'has_options',
        sort: 'desc',
      },
      {
        field: 'sub_event_type',
        sort: 'desc',
      },
    ],
  },
  columns: {
    columnVisibilityModel: {
      id: false,
    },
  },
}

export default function AdminSubEvents() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { eventId } = useParams()
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')

  const showStatistics = useContextShowStatistics()

  const [getSubEventQRCode] = useLazyGetSubEventQRCodeQuery()

  const { event, subEvents } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >

  const subEventsTypeOptions = useSubEventsTypeOptions(t)
  const subEventsStatusTypeOptions = useSubEventsStatusTypeOptions(t)
  const locationsOptions = useLocationDataOptions()

  const roomsOptions = useRoomsDataOptions(event?.location, locationsOptions)

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          const row = params.row as SubEvents
          return (
            <Stack direction="row">
              {row.has_options && (
                <Tooltip title={t('Générer le QR code intervention')}>
                  <IconButton
                    color="primary"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      void getSubEventQRCode({ eventId: Number(eventId), subEventId: row.id })
                    }}
                  >
                    <QrCode2Icon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t("Éditer l'intervention")}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/admin/events/${eventId}/sessions/subEvent/${row.id}`)
                  }}
                >
                  <EditNoteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      { field: 'id', headerName: t('ID'), width: 60 },
      {
        field: 'name',
        headerName: t('Nom'),
        width: 300,
      },
      {
        field: 'sub_event_type',
        headerName: t('Type'),
        width: 150,
        type: 'singleSelect',
        valueOptions: subEventsTypeOptions,
        renderCell: params => {
          const type = getOption(subEventsTypeOptions, undefined, params.value as SubEventType)
          return (
            <Chip
              sx={theme => ({
                backgroundColor: type?.color,
                color: theme.palette.getContrastText(type?.color ?? 'inherit'),
              })}
              size="small"
              label={type?.label ?? 'NC'}
            />
          )
        },
      },
      {
        field: 'has_options',
        headerName: t('Options'),
        width: 150,
        type: 'boolean',
      },
      {
        field: 'status',
        headerName: t('Statut'),
        width: 150,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: subEventsStatusTypeOptions,
        renderCell: params => (
          <SubEventStatusTag size="small" status={params.value as SubEvents['status']} />
        ),
      },
      {
        field: 'start',
        headerName: t('Début'),
        width: 150,
        type: 'dateTime',
        valueGetter: value => value && new Date(value),
        valueFormatter: value => (value ? formatDateTime(value) : 'NC'),
      },
      {
        field: 'end',
        headerName: t('Fin'),
        width: 150,
        type: 'dateTime',
        valueGetter: value => value && new Date(value),
        valueFormatter: value => (value ? formatDateTime(value) : 'NC'),
      },
      {
        field: 'rooms',
        headerName: t('Salles'),
        width: 300,
        valueOptions: roomsOptions.map(room => ({
          value: room.id,
          label: room.name,
        })),
        valueGetter: (value?: number[]) =>
          value
            ?.map(val => roomsOptions.find(el => el.id === val)?.name)
            .filter(el => el !== undefined)
            .join(', ') ?? 'NC',
        // TODO implement filter with multipleSelect
        // type: 'singleSelect',
        // valueGetter: (value?: number[]) =>
        //   value
        //     ?.map(val => {
        //       const res = availableRooms.find(el => el.id === val)
        //       return res
        //         ? {
        //             value: res?.id,
        //             label: res?.name,
        //           }
        //         : undefined
        //     })
        //     .filter(el => el !== undefined),
        // valueFormatter: (value?: { label: string }[]) =>
        //   value?.map(val => val.label).join(', ') ?? 'NC',
      },
    ],
    [
      t,
      subEventsTypeOptions,
      subEventsStatusTypeOptions,
      getSubEventQRCode,
      eventId,
      navigate,
      roomsOptions,
    ]
  )

  return (
    <Stack
      sx={theme => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.boxShadows.main,
      })}
      height="inherit"
      overflow="hidden auto"
      padding="0.5rem"
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">{t('Détails événement')}</Typography>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && <AdminEventDetails event={event} />}

      <Divider />
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ margin: '1rem 0' }}
        >
          <h3>{t('Liste des interventions')} :</h3>
          <ViewButtonGroup />
        </Stack>
        <Stack
          sx={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
            overflow: 'auto',
          }}
        >
          <StripedDataGrid
            rows={subEvents}
            columns={columns}
            initialState={initialState}
            count={subEvents?.length}
            paginationMode="client"
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
